import {
  Stepper,
  StepperProvider,
  useStepper,
} from '@/components/Molecules/Stepper'
import { buildPath } from '@/routes'
import { PropsWithChildren, useMemo } from 'react'
import { LicensingAgencyNew } from '../hooks/licensing-agency-provider'

export const Steps = {
  Agency: {
    id: 0,
    path: (agencyId: string) =>
      buildPath('/applications/licensing-agency/:agencyId/business-info', {
        agencyId,
      }),
    title: 'Agency Information',
  },
  Documentation: {
    id: 1,
    path: (agencyId: string) =>
      buildPath('/applications/licensing-agency/:agencyId/documentation', {
        agencyId,
      }),
    title: 'Documentation',
  },
  Certify: {
    id: 2,
    path: (agencyId: string) =>
      buildPath('/applications/licensing-agency/:agencyId/certify', {
        agencyId,
      }),
    title: 'Certify & Submit',
  },
} as const

export interface LicensingAgencyStepsProps {
  agencyId?: string
}

export function LicensingAgencySteps({
  agencyId,
  children,
}: PropsWithChildren<LicensingAgencyStepsProps>) {
  const orderedSteps = useMemo(
    () =>
      Object.values(resolveSteps(Steps, agencyId || LicensingAgencyNew)).sort(
        (a, b) => a.id - b.id
      ),
    [agencyId] // Memoize once
  )

  return (
    <StepperProvider steps={orderedSteps}>
      {agencyId && (
        <Stepper
          allComplete={buildPath(
            '/applications/licensing-agency/:agencyId/success',
            { agencyId }
          )}
          canJumpBack
        />
      )}
      {children}
    </StepperProvider>
  )
}

type ResolvedSteps<T extends Record<string, { path: (id: string) => string }>> =
  {
    [K in keyof T]: Omit<T[K], 'path'> & { path: string }
  }

const resolveSteps = <
  T extends Record<string, { path: (id: string) => string }>,
>(
  steps: T,
  agencyId: string
): ResolvedSteps<T> => {
  const out = {} as ResolvedSteps<T>
  for (const key in steps) {
    const step = steps[key]
    out[key] = { ...step, path: step.path(agencyId) }
  }
  return out
}

export function useLicensingAgencyStepper(agencyId?: string) {
  const resolved = resolveSteps(Steps, agencyId || '')
  const orderedSteps = Object.values(resolved).sort((a, b) => a.id - b.id)

  return {
    Steps: resolved,
    ...useStepper<typeof orderedSteps>(),
  }
}
